import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import PawPrint from "@/components/PawPrint";
import DisclaimerBanner from "@/components/DisclaimerBanner";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

const NOTIF_SETTINGS = [
  { id: "rappels",  label: "Rappels vaccins & soins",   sub: "Alertes avant échéance",          default: true },
  { id: "semaine",  label: "Récap hebdomadaire",         sub: "Chaque lundi matin",              default: true },
  { id: "conseils", label: "Conseils saisonniers",       sub: "Selon la météo et la saison",     default: false },
  { id: "promo",    label: "Nouveautés PawCare",         sub: "Fonctionnalités et mises à jour", default: false },
];

const PRIVACY_SETTINGS = [
  { id: "analytics", label: "Partage anonyme de données", sub: "Aide à améliorer PawCare", default: true },
  { id: "history",   label: "Conservation des échanges",  sub: "Historique des consultations", default: true },
];

/* ====== COMPONENTS ====== */

function SectionCard({ title, children, noPad = false }) {
  return (
    <div className="bg-white rounded-[22px] overflow-hidden shadow-soft border-[1.5px] border-black/[0.04] mb-3.5">
      {title && (
        <div className="font-black text-[13px] text-app-muted px-[18px] pt-3.5 uppercase tracking-wide">
          {title}
        </div>
      )}
      <div className={noPad ? "" : "px-[18px] pt-3 pb-4"}>{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      className="w-[46px] h-[26px] rounded-full cursor-pointer relative shrink-0 transition-colors duration-300"
      style={{
        background: value
          ? "linear-gradient(135deg, #FFB347, #FF5C8D)"
          : "#E0E0E0",
        boxShadow: value ? "0 2px 10px rgba(255,112,85,0.4)" : "none",
      }}
    >
      <div
        className="w-5 h-5 rounded-full bg-white absolute top-[3px] shadow-md transition-[left] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
        style={{ left: value ? 23 : 3 }}
      />
    </div>
  );
}

function SettingRow({ icon, label, sub, value, onChange, type = "toggle", danger = false }) {
  return (
    <div className="flex items-center gap-3.5 px-[18px] py-[13px] border-b border-black/[0.04]">
      <div
        className={`w-[38px] h-[38px] rounded-xl shrink-0 flex items-center justify-center text-lg ${
          danger ? "bg-severity-red-bg" : ""
        }`}
        style={
          danger
            ? undefined
            : { background: "linear-gradient(135deg, rgba(255,179,71,0.15), rgba(255,112,85,0.1))" }
        }
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className={`font-extrabold text-sm ${danger ? "text-severity-red" : "text-app-text"}`}>
          {label}
        </div>
        {sub && (
          <div className="text-[11.5px] text-app-muted font-semibold mt-px">{sub}</div>
        )}
      </div>
      {type === "toggle" && <Toggle value={value} onChange={onChange} />}
      {type === "arrow" && <span className="text-lg text-app-subtle">{"\u203A"}</span>}
    </div>
  );
}

function StatBubble({ icon, value, label, gradient }) {
  return (
    <div className="flex-1 bg-white/70 rounded-[18px] py-3.5 px-2.5 text-center border-[1.5px] border-white/90">
      <div className="text-2xl mb-1">{icon}</div>
      <div
        className="font-display text-[22px] leading-none"
        style={{
          background: gradient,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {value}
      </div>
      <div className="text-[11px] text-[#999] font-bold mt-[3px]">{label}</div>
    </div>
  );
}

/* ====== FEEDBACK ====== */
function FeedbackCard({ API }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    API.get("/api/feedback").then(res => {
      if (res.data) {
        setRating(res.data.rating);
        setComment(res.data.comment || "");
        setSubmitted(true);
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [API]);

  const handleSubmit = async () => {
    if (rating === 0) return;
    setLoading(true);
    try {
      await API.post("/api/feedback", { rating, comment });
      setSubmitted(true);
    } catch { /* silent */ }
    setLoading(false);
  };

  if (!loaded) return null;

  return (
    <div className="bg-white rounded-[22px] overflow-hidden shadow-soft border-[1.5px] border-pawcare-orange/20 mb-3.5">
      <div className="px-[18px] pt-4 pb-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">🧪</span>
          <span className="font-black text-[13px] text-app-muted uppercase tracking-wide">Version Alpha</span>
        </div>
        <div className="text-[13.5px] text-app-text font-bold leading-relaxed">
          {submitted
            ? "Merci pour votre avis ! Vous pouvez le modifier a tout moment."
            : "Aidez Samuel a ameliorer PawCare en laissant votre avis !"}
        </div>
      </div>
      <div className="px-[18px] pt-3 pb-4">
        {/* Star rating */}
        <div className="flex gap-1.5 mb-3">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              onClick={() => { setRating(star); setSubmitted(false); }}
              className="bg-transparent border-none cursor-pointer text-[32px] transition-transform duration-200 hover:scale-110 active:scale-95 p-0"
              style={{ filter: star <= rating ? "none" : "grayscale(1) opacity(0.3)" }}
            >
              ⭐
            </button>
          ))}
        </div>
        {/* Comment */}
        <textarea
          value={comment}
          onChange={e => { setComment(e.target.value); setSubmitted(false); }}
          placeholder="Un bug ? Une idee ? Dites-nous tout..."
          rows={3}
          className="w-full rounded-2xl border-[1.5px] border-gray-200 bg-gray-50 px-4 py-3 text-[13.5px] font-semibold text-app-text resize-none outline-none focus:border-pawcare-orange/50 transition-colors"
        />
        <button
          onClick={handleSubmit}
          disabled={rating === 0 || loading || submitted}
          className="w-full mt-2.5 py-3 rounded-2xl border-none cursor-pointer font-black text-[14px] transition-all duration-200 font-sans"
          style={{
            background: submitted ? "#E8F5E9" : "linear-gradient(135deg, #FFB347, #FF5C8D)",
            color: submitted ? "#2E7D32" : "white",
            opacity: rating === 0 ? 0.5 : 1,
          }}
        >
          {loading ? "Envoi..." : submitted ? "✓ Avis envoye !" : "Envoyer mon avis"}
        </button>
      </div>
    </div>
  );
}

/* ====== PAGE ====== */
export default function ProfilPage() {
  const { user, logout, API } = useAuth();
  const navigate = useNavigate();

  const [notifs, setNotifs] = useState(
    Object.fromEntries(NOTIF_SETTINGS.map((n) => [n.id, n.default]))
  );
  const [privacy, setPrivacy] = useState(
    Object.fromEntries(PRIVACY_SETTINGS.map((n) => [n.id, n.default]))
  );
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [nameInput, setNameInput] = useState(user?.name || "");
  const [stats, setStats] = useState({ consultations: 0, animals: 0, streak: 0 });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const pushSupported = "serviceWorker" in navigator && "PushManager" in window;

  // Check push subscription status
  useEffect(() => {
    if (!pushSupported) return;
    API.get("/api/notifications/status").then(res => {
      setPushEnabled(res.data?.subscribed || false);
    }).catch(() => {});
  }, [API, pushSupported]);

  const togglePush = async (enable) => {
    if (!pushSupported) return;
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (enable) {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          setPushLoading(false);
          return;
        }
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        const applicationServerKey = urlBase64ToUint8Array(vapidKey);
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
        const subJson = sub.toJSON();
        await API.post("/api/notifications/subscribe", {
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        });
        setPushEnabled(true);
      } else {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const subJson = sub.toJSON();
          await API.delete("/api/notifications/subscribe", {
            data: { endpoint: subJson.endpoint, keys: subJson.keys },
          });
          await sub.unsubscribe();
        }
        setPushEnabled(false);
      }
    } catch (err) {
      console.error("Push toggle error:", err);
    }
    setPushLoading(false);
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [animalsRes, consultsRes] = await Promise.all([
          API.get("/api/animals"),
          API.get("/api/consultations"),
        ]);
        const animalsCount = animalsRes.data.length;
        const consultsCount = consultsRes.data.length;

        // Calcul série active : jours consécutifs avec au moins une consultation
        let streak = 0;
        if (consultsRes.data.length > 0) {
          const dates = consultsRes.data
            .map(c => c.started_at ? new Date(c.started_at).toDateString() : null)
            .filter(Boolean);
          const uniqueDates = [...new Set(dates)].sort((a, b) => new Date(b) - new Date(a));

          const today = new Date().toDateString();
          const yesterday = new Date(Date.now() - 86400000).toDateString();

          if (uniqueDates[0] === today || uniqueDates[0] === yesterday) {
            streak = 1;
            for (let i = 1; i < uniqueDates.length; i++) {
              const prev = new Date(uniqueDates[i - 1]);
              const curr = new Date(uniqueDates[i]);
              const diff = (prev - curr) / 86400000;
              if (diff <= 1) streak++;
              else break;
            }
          }
        }

        setStats({ consultations: consultsCount, animals: animalsCount, streak });
      } catch {
        // silently fail
      }
    };
    fetchStats();
  }, [API]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : "";

  return (
    <div className="min-h-screen bg-app-gradient animate-fade-in">
      {/* -- HEADER -- */}
      <div className="bg-pawcare-gradient px-6 shadow-pawcare" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <PawPrint size={34} color="rgba(255,255,255,0.92)" />
            <div className="font-display text-[22px] text-white leading-none">
              Mon profil
            </div>
          </div>
          <button
            onClick={() => {
              setEditMode((v) => !v);
              setNameInput(name);
            }}
            className="bg-white/[0.22] border-[1.5px] border-white/40 rounded-xl px-3.5 py-[7px] text-white font-extrabold text-[13px] cursor-pointer"
          >
            {editMode ? "Annuler" : "✏️ Modifier"}
          </button>
        </div>

        {/* Avatar + identity */}
        <div className="flex items-center gap-4 pb-6">
          <div className="relative">
            <div className="w-[76px] h-[76px] rounded-full bg-white/25 flex items-center justify-center text-[42px] border-[3px] border-white/60 backdrop-blur-sm">
              👩
            </div>
            {editMode && (
              <div className="absolute bottom-0 right-0 w-[26px] h-[26px] rounded-full bg-white flex items-center justify-center text-[13px] shadow-md cursor-pointer">
                📷
              </div>
            )}
          </div>
          <div className="flex-1">
            {editMode ? (
              <input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="font-display text-[22px] text-white bg-white/20 border-[1.5px] border-white/50 rounded-[10px] py-1 px-2.5 outline-none w-full"
              />
            ) : (
              <div className="font-display text-2xl text-white leading-none">
                {name}
              </div>
            )}
            <div className="text-[13px] text-white/80 font-semibold mt-1">
              {user?.email || ""}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="bg-white/[0.22] rounded-full px-2.5 py-[3px] text-[11px] font-extrabold text-white border border-white/30">
                🌟 Gratuit
              </span>
              {memberSince && (
                <span className="text-[11px] text-white/65 font-semibold">
                  Membre depuis {memberSince}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white/15 backdrop-blur-md rounded-t-[20px] px-4 pt-4 flex gap-2.5">
          <StatBubble
            icon="🩺"
            value={stats.consultations}
            label="Consultations"
            gradient="linear-gradient(135deg, #FFB347, #FF5C8D)"
          />
          <StatBubble
            icon="🐾"
            value={stats.animals}
            label="Animaux"
            gradient="linear-gradient(135deg, #FF7055, #FF5C8D)"
          />
          <StatBubble
            icon="🔥"
            value={stats.streak > 0 ? `${stats.streak}j` : "0"}
            label="Série active"
            gradient="linear-gradient(135deg, #FFB347, #FF8C42)"
          />
        </div>
      </div>

      {/* -- Save button when editing -- */}
      {editMode && (
        <div className="px-4 pt-3.5 max-w-[540px] mx-auto">
          <button
            onClick={() => {
              setName(nameInput);
              setEditMode(false);
            }}
            className="w-full py-3.5 rounded-2xl border-none cursor-pointer bg-pawcare-gradient text-white font-black text-[15px] shadow-pawcare flex items-center justify-center gap-2"
          >
            ✓ Enregistrer les modifications
          </button>
        </div>
      )}

      {/* -- CONTENT -- */}
      <div className="px-4 pt-[18px] max-w-[540px] mx-auto">
        {/* Subscription upsell */}
        <div className="rounded-[22px] px-5 py-[18px] mb-3.5 border-[1.5px] border-pawcare-orange/30 flex items-center gap-3.5"
          style={{ background: "linear-gradient(135deg, #FFF4E0, #FFF0F8)" }}
        >
          <div className="w-12 h-12 rounded-[14px] bg-pawcare-gradient flex items-center justify-center text-2xl shrink-0">
            ⭐
          </div>
          <div className="flex-1">
            <div className="font-black text-[15px] text-app-text">
              PawCare Pro
            </div>
            <div className="text-xs text-app-muted font-semibold mt-0.5">
              Consultations illimitées, analyse photo avancée, rappels intelligents
            </div>
          </div>
          <span className="bg-pawcare-gradient border-none rounded-[14px] px-4 py-2.5 text-white font-black text-[13px] shrink-0 shadow-pawcare opacity-80">
            Bientôt
          </span>
        </div>

        {/* Notifications */}
        <SectionCard title="🔔 Notifications" noPad>
          {pushSupported && (
            <SettingRow
              icon="📲"
              label="Notifications push"
              sub={pushEnabled ? "Activees sur cet appareil" : "Recevez alertes et rappels"}
              value={pushEnabled}
              onChange={(v) => togglePush(v)}
            />
          )}
          {!pushSupported && (
            <div className="px-[18px] py-3 text-[12px] text-app-muted font-semibold">
              Les notifications push ne sont pas supportees sur ce navigateur.
              {/iPhone|iPad/.test(navigator.userAgent) && " Sur iOS, ajoutez d'abord PawCare a l'ecran d'accueil via Safari."}
            </div>
          )}
          {NOTIF_SETTINGS.map((n) => (
            <SettingRow
              key={n.id}
              icon={
                n.id === "rappels"
                  ? "💉"
                  : n.id === "semaine"
                  ? "📅"
                  : n.id === "conseils"
                  ? "🌤️"
                  : "🎉"
              }
              label={n.label}
              sub={n.sub}
              value={notifs[n.id]}
              onChange={(v) => setNotifs((p) => ({ ...p, [n.id]: v }))}
            />
          ))}
        </SectionCard>

        {/* Privacy */}
        <SectionCard title="🔒 Confidentialité" noPad>
          {PRIVACY_SETTINGS.map((n) => (
            <SettingRow
              key={n.id}
              icon={n.id === "analytics" ? "📊" : "💬"}
              label={n.label}
              sub={n.sub}
              value={privacy[n.id]}
              onChange={(v) => setPrivacy((p) => ({ ...p, [n.id]: v }))}
            />
          ))}
          <div onClick={() => navigate("/legal?tab=privacy")} className="cursor-pointer">
            <SettingRow icon="📄" label="Politique de confidentialité" type="arrow" />
          </div>
          <div onClick={() => navigate("/legal")} className="cursor-pointer">
            <SettingRow icon="📋" label="Conditions d'utilisation" type="arrow" />
          </div>
        </SectionCard>

        {/* Account */}
        <SectionCard title="⚙️ Compte" noPad>
          <SettingRow icon="📤" label="Exporter mes données" sub="Format JSON — RGPD" type="arrow" />
          <SettingRow icon="🔑" label="Changer de mot de passe" type="arrow" />
          <SettingRow icon="🗑️" label="Supprimer mon compte" sub="Action irréversible" type="arrow" danger />
        </SectionCard>

        {/* Feedback alpha */}
        <FeedbackCard API={API} />

        {/* About */}
        <SectionCard title="ℹ️ À propos" noPad>
          <SettingRow icon="💬" label="Envoyer un retour" sub="Bugs, suggestions…" type="arrow" />
          <SettingRow icon="📱" label="Version" sub="PawCare Alpha — Mars 2026" type="arrow" />
          <div className="px-[18px] py-3 border-t border-black/[0.04] text-center">
            <div className="text-[12px] text-app-muted font-semibold">
              Cree avec amour par <span className="font-black text-pawcare-orange-dark">Samuel</span>
            </div>
          </div>
        </SectionCard>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full py-4 rounded-[18px] border-[1.5px] border-[#FFD0D0] bg-severity-red-bg/60 cursor-pointer font-black text-[15px] text-severity-red mb-3.5 flex items-center justify-center gap-2 transition-all duration-200 hover:bg-severity-red-bg hover:shadow-[0_4px_14px_rgba(229,83,83,0.15)]"
        >
          <span className="text-lg">🚪</span> Se déconnecter
        </button>

        {/* Disclaimer */}
        <DisclaimerBanner />
      </div>
    </div>
  );
}
